<template>
  <div class="mobile-root">

    <!-- ───────────────────────────────────────────
         STICKY COMPACT HEADER
    ─────────────────────────────────────────────── -->
    <header class="m-header">
      <div class="m-header-inner">
        <!-- Brand -->
        <div class="m-brand">
          <span class="m-brand-dot"></span>
          <span class="m-brand-name">Muster</span>
        </div>
        <!-- Right actions -->
        <div class="m-header-right">
          <span class="m-user-name">{{ authStore.user?.name }}</span>
          <button class="m-icon-btn" title="Notifications">
            <i class="pi pi-bell"></i>
            <span v-if="unreadCount > 0" class="m-bell-badge">{{ unreadCount }}</span>
          </button>
          <button class="m-icon-btn m-logout-btn" @click="handleLogout" title="Logout">
            <i class="pi pi-sign-out"></i>
          </button>
        </div>
      </div>
    </header>

    <!-- ───────────────────────────────────────────
         TODAY'S PULSE STRIP
    ─────────────────────────────────────────────── -->
    <div class="m-pulse-strip">
      <button class="m-pulse-chip" @click="scrollTo('m-tasks')">
        <span class="m-chip-emoji">✅</span>
        <span class="m-chip-label">Tasks Due</span>
        <span class="m-chip-count">{{ tasksDueToday.length }}</span>
      </button>
      <button class="m-pulse-chip" @click="scrollTo('m-circulars')">
        <span class="m-chip-emoji">📢</span>
        <span class="m-chip-label">Circulars</span>
        <span class="m-chip-count">{{ orgCirculars.length + deptCirculars.length }}</span>
      </button>
      <button class="m-pulse-chip" @click="scrollTo('m-news')">
        <span class="m-chip-emoji">📰</span>
        <span class="m-chip-label">News</span>
        <span class="m-chip-count">{{ news.length }}</span>
      </button>
      <button class="m-pulse-chip" @click="scrollTo('m-sop')">
        <span class="m-chip-emoji">📋</span>
        <span class="m-chip-label">SOPs</span>
        <span class="m-chip-count">{{ sopCount }}</span>
      </button>
      <button class="m-pulse-chip" @click="scrollTo('m-birthdays')">
        <span class="m-chip-emoji">🎂</span>
        <span class="m-chip-label">Birthdays</span>
        <span class="m-chip-count">{{ birthdays.length || '—' }}</span>
      </button>
    </div>

    <!-- ───────────────────────────────────────────
         MAIN SCROLLABLE CONTENT
    ─────────────────────────────────────────────── -->
    <main class="m-main-content">

      <!-- ══════════════════════════════════════════
           SECTION: TASKS
      ══════════════════════════════════════════════ -->
      <section id="m-tasks" class="m-section">
        <div class="m-section-header">
          <span class="m-section-title">My Tasks</span>
          <router-link to="/Task" class="m-view-all">View All →</router-link>
        </div>

        <!-- Stat chips row -->
        <div class="m-stat-row">
          <div class="m-stat-chip">
            <span class="m-stat-value">{{ taskStats.pending }}</span>
            <span class="m-stat-label">Pending</span>
          </div>
          <div class="m-stat-chip m-stat-inprogress">
            <span class="m-stat-value">{{ taskStats.inProgress }}</span>
            <span class="m-stat-label">In Progress</span>
          </div>
          <div class="m-stat-chip m-stat-overdue">
            <span class="m-stat-value">{{ taskStats.overdue }}</span>
            <span class="m-stat-label">Overdue</span>
          </div>
          <div class="m-stat-chip m-stat-total">
            <span class="m-stat-value">{{ taskStats.total }}</span>
            <span class="m-stat-label">Total</span>
          </div>
        </div>

        <!-- Tab switcher -->
        <div class="m-tab-bar">
          <button
            class="m-tab-btn"
            :class="{ 'm-tab-active': activeTaskTab === 'my' }"
            @click="switchTaskTab('my')"
          >My Tasks</button>
          <button
            class="m-tab-btn"
            :class="{ 'm-tab-active': activeTaskTab === 'dept' }"
            @click="switchTaskTab('dept')"
          >Dept Tasks</button>
        </div>

        <!-- Task cards -->
        <div class="m-task-list" v-if="!tasksLoading">
          <template v-if="sortedTasks.length > 0">
            <div
              v-for="task in sortedTasks.slice(0, 20)"
              :key="task.TaskId || task.Id"
              class="m-task-card"
            >
              <div class="m-task-card-top">
                <span class="m-badge" :class="priorityClass(task.TaskPriorityName)">
                  {{ task.TaskPriorityName || 'Normal' }}
                </span>
                <span class="m-task-name">{{ task.TaskName }}</span>
              </div>
              <div class="m-task-card-bottom">
                <span class="m-task-dept">{{ task.DepartmentName }}</span>
                <div class="m-task-card-meta">
                  <span class="m-badge" :class="statusClass(task.TaskStatusName)">
                    {{ task.TaskStatusName || 'Pending' }}
                  </span>
                  <span
                    class="m-due-chip"
                    :class="dueCellClass(task.EndDate, task.TaskStatusName)"
                  >{{ timeRemaining(task.EndDate) }}</span>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="m-empty-state">
            <i class="pi pi-check-circle m-empty-icon"></i>
            <p>No tasks found</p>
          </div>
        </div>
        <div v-else class="m-loading-row">
          <i class="pi pi-spin pi-spinner"></i> Loading tasks…
        </div>
      </section>

      <!-- ══════════════════════════════════════════
           SECTION: BIRTHDAYS
      ══════════════════════════════════════════════ -->
      <section id="m-birthdays" class="m-section" v-if="birthdays.length > 0">
        <div class="m-section-header">
          <span class="m-section-title">🎂 Birthdays</span>
          <span class="m-chip-count-header">{{ todayBirthdays.length }} today</span>
        </div>

        <!-- Today's birthdays -->
        <div v-if="todayBirthdays.length > 0" class="m-card m-birthday-today-card">
          <div class="m-birthday-today-header">Today's Birthdays</div>
          <div
            v-for="(b, idx) in todayBirthdays"
            :key="'today-' + idx"
            class="m-birthday-row"
          >
            <div class="m-birthday-avatar">🎂</div>
            <div class="m-birthday-info">
              <span class="m-birthday-name">{{ b.EmployeeName || b.Name }}</span>
              <span class="m-birthday-dept">{{ b.DepartmentName || b.Department }} · {{ b.EmployeeCode || b.Code }}</span>
            </div>
          </div>
        </div>

        <!-- Upcoming birthdays -->
        <div v-if="upcomingBirthdays.length > 0" class="m-card">
          <div class="m-birthday-upcoming-header">Upcoming (Next 7 Days)</div>
          <div
            v-for="(b, idx) in upcomingBirthdays.slice(0, 5)"
            :key="'upcoming-' + idx"
            class="m-birthday-row"
          >
            <div class="m-birthday-avatar-upcoming">🎁</div>
            <div class="m-birthday-info">
              <span class="m-birthday-name">{{ b.EmployeeName || b.Name }}</span>
              <span class="m-birthday-dept">{{ b.DepartmentName || b.Department }} · {{ formatBirthdayDate(b) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════
           SECTION: CIRCULARS
      ══════════════════════════════════════════════ -->
      <section id="m-circulars" class="m-section">
        <div class="m-section-header">
          <span class="m-section-title">Circulars</span>
          <router-link to="/circulars" class="m-view-all">View All →</router-link>
        </div>

        <!-- Admin dept filter -->
        <div v-if="isAdmin" class="m-admin-row">
          <button class="m-filter-btn" @click="showDeptDrawer = true">
            <i class="pi pi-filter"></i> Filter by Department
          </button>
        </div>

        <!-- Merged circular feed -->
        <div class="m-card">
          <template v-if="mergedCirculars.length > 0">
            <div
              v-for="(circ, idx) in mergedCirculars"
              :key="circ.CircularId || idx"
              class="m-circular-row"
              @click="selectedCircular = circ; showCircularDrawer = true"
            >
              <span
                class="m-circ-dot"
                :class="circ._type === 'org' ? 'm-circ-dot-org' : 'm-circ-dot-dept'"
              ></span>
              <div class="m-circular-info">
                <span class="m-circular-title">{{ circ.Title }}</span>
                <div class="m-circular-meta">
                  <span class="m-circular-date">{{ formatDate(circ.PublishedDate || circ.CreatedDate) }}</span>
                  <span v-if="circ.DepartmentName" class="m-circular-dept">· {{ circ.DepartmentName }}</span>
                </div>
              </div>
              <i v-if="circ.FullAttachmentPath" class="pi pi-paperclip m-attach-icon"></i>
            </div>
          </template>
          <div v-else class="m-empty-state">
            <i class="pi pi-megaphone m-empty-icon"></i>
            <p>No circulars available</p>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════
           SECTION: NEWS & EVENTS
      ══════════════════════════════════════════════ -->
      <section id="m-news" class="m-section">
        <div class="m-section-header">
          <span class="m-section-title">News &amp; Events</span>
          <button class="m-view-all">View All →</button>
        </div>

        <!-- Category pills -->
        <div class="m-pills-scroll">
          <button
            v-for="cat in newsCategories"
            :key="cat"
            class="m-pill"
            :class="{ 'm-pill-active': selectedNewsCategory === cat }"
            @click="selectNewsCategory(cat)"
          >{{ cat }}</button>
        </div>

        <!-- News cards -->
        <div v-if="news.length > 0">
          <div
            v-for="(item, idx) in news.slice(0, 5)"
            :key="item.NewsId || idx"
            class="m-news-card"
            @click="selectedNews = item; showNewsDrawer = true"
          >
            <div class="m-news-card-top">
              <span class="m-news-cat-badge" :class="newsCategoryClass(item.Category)">
                {{ item.Category || 'General' }}
              </span>
              <span class="m-news-type-label">{{ item.IsEvent ? 'Event' : 'News' }}</span>
            </div>
            <p class="m-news-title">{{ item.Title }}</p>
            <p class="m-news-date">{{ formatDate(item.PublishedDate || item.CreatedDate) }}</p>
            <p class="m-news-desc">{{ item.Description || item.Content }}</p>
          </div>
        </div>
        <div v-else class="m-empty-state">
          <i class="pi pi-newspaper m-empty-icon"></i>
          <p>No news available</p>
        </div>
      </section>

      <!-- ══════════════════════════════════════════
           SECTION: APPLICATIONS
      ══════════════════════════════════════════════ -->
      <section id="m-apps" class="m-section">
        <div class="m-section-header">
          <span class="m-section-title">Applications</span>
        </div>
        <div v-if="loading" class="m-loading-row">
          <i class="pi pi-spin pi-spinner"></i> Loading apps…
        </div>
        <div v-else class="m-apps-grid">
          <button
            v-for="mod in accessibleModules"
            :key="mod.id"
            class="m-app-item"
            @click="handleModuleSelect(mod)"
          >
            <div class="m-app-icon">
              <img
                v-if="mod.icon && mod.icon.includes('/')"
                :src="'/' + mod.icon"
                :alt="mod.name"
                class="m-app-logo"
              />
              <i v-else :class="mod.icon || 'pi pi-th-large'"></i>
            </div>
            <span class="m-app-name">{{ mod.name }}</span>
          </button>
        </div>
        <div v-if="!loading && accessibleModules.length === 0" class="m-empty-state">
          <i class="pi pi-th-large m-empty-icon"></i>
          <p>No applications available</p>
        </div>
      </section>

      <!-- ══════════════════════════════════════════
           SECTION: SOP
      ══════════════════════════════════════════════ -->
      <section id="m-sop" class="m-section">
        <div class="m-section-header">
          <span class="m-section-title">SOP Quick Access</span>
          <router-link to="/sop" class="m-view-all">View All →</router-link>
        </div>

        <!-- Search input -->
        <div class="m-search-wrap">
          <i class="pi pi-search m-search-icon"></i>
          <input
            v-model="sopSearchTerm"
            class="m-search-input"
            type="text"
            placeholder="Search SOPs…"
            @input="onSOPSearch"
          />
          <button v-if="sopSearchTerm" class="m-search-clear" @click="clearSOPSearch">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!-- SOP category pills -->
        <div class="m-pills-scroll">
          <button
            v-for="cat in sopCategories"
            :key="cat"
            class="m-pill"
            :class="{ 'm-pill-active': selectedCategory === cat }"
            @click="selectCategory(cat)"
          >{{ cat }}</button>
        </div>

        <!-- SOP list -->
        <div class="m-card" v-if="!sopLoading">
          <template v-if="filteredSOPs.slice(0, 8).length > 0">
            <div
              v-for="(sop, idx) in filteredSOPs.slice(0, 8)"
              :key="sop.SOPId || idx"
              class="m-sop-row"
            >
              <div class="m-sop-file-icon">
                <i class="pi pi-file-pdf"></i>
              </div>
              <div class="m-sop-info">
                <span class="m-sop-title">{{ sop.Title }}</span>
                <div class="m-sop-meta">
                  <span class="m-sop-cat-badge">{{ sop.Category }}</span>
                  <span v-if="sop.Version" class="m-sop-version">v{{ sop.Version.replace(/^v/i, '') }}</span>
                </div>
              </div>
              <a
                v-if="sop.FullDocumentPath"
                :href="sop.FullDocumentPath"
                target="_blank"
                class="m-sop-download"
                @click.stop
              >
                <i class="pi pi-download"></i>
              </a>
            </div>
          </template>
          <div v-else class="m-empty-state">
            <i class="pi pi-file m-empty-icon"></i>
            <p>No SOPs found</p>
          </div>
        </div>
        <div v-else class="m-loading-row">
          <i class="pi pi-spin pi-spinner"></i> Loading SOPs…
        </div>
      </section>

      <!-- Bottom spacer for nav bar -->
      <div class="m-bottom-spacer"></div>
    </main>

    <!-- ───────────────────────────────────────────
         DEPARTMENT FILTER DRAWER (Admin)
    ─────────────────────────────────────────────── -->
    <ONEDrawer
      v-model:visible="showDeptDrawer"
      position="bottom"
      :style="{ height: '280px' }"
      :modal="true"
      :dismissable-mask="true"
    >
      <template #header>
        <span class="m-drawer-title">Select Department</span>
      </template>
      <div class="m-drawer-body">
        <ONEDropdown
          v-model="selectedDeptId"
          :options="deptOptions"
          option-label="label"
          option-value="value"
          placeholder="All Departments"
          :show-clear="true"
          class="w-full"
        />
        <button class="m-drawer-close-btn" @click="showDeptDrawer = false">Close</button>
      </div>
    </ONEDrawer>

    <!-- ───────────────────────────────────────────
         CIRCULAR DETAIL DRAWER
    ─────────────────────────────────────────────── -->
    <ONEDrawer
      v-model:visible="showCircularDrawer"
      position="bottom"
      :style="{ height: '70vh' }"
      :modal="true"
      :dismissable-mask="true"
    >
      <template #header>
        <span class="m-drawer-title">Circular</span>
      </template>
      <div class="m-drawer-body" v-if="selectedCircular">
        <div class="m-detail-badges">
          <span
            class="m-badge"
            :class="selectedCircular._type === 'org' ? 'm-badge-org' : 'm-badge-dept'"
          >{{ selectedCircular._type === 'org' ? 'Org' : 'Dept' }}</span>
          <span v-if="selectedCircular.DepartmentName" class="m-detail-dept">
            {{ selectedCircular.DepartmentName }}
          </span>
        </div>
        <h3 class="m-detail-title">{{ selectedCircular.Title }}</h3>
        <p class="m-detail-date">
          {{ formatDate(selectedCircular.PublishedDate || selectedCircular.CreatedDate) }}
        </p>
        <p v-if="selectedCircular.Content" class="m-detail-text">{{ selectedCircular.Content }}</p>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="selectedCircular.ContentHtml" class="m-detail-html" v-html="selectedCircular.ContentHtml"></div>
        <a
          v-if="selectedCircular.FullAttachmentPath"
          :href="selectedCircular.FullAttachmentPath"
          target="_blank"
          class="m-download-btn"
        >
          <i class="pi pi-download"></i> Download Attachment
        </a>
      </div>
    </ONEDrawer>

    <!-- ───────────────────────────────────────────
         NEWS DETAIL DRAWER
    ─────────────────────────────────────────────── -->
    <ONEDrawer
      v-model:visible="showNewsDrawer"
      position="bottom"
      :style="{ height: '80vh' }"
      :modal="true"
      :dismissable-mask="true"
    >
      <template #header>
        <span class="m-drawer-title">{{ selectedNews?.IsEvent ? 'Event' : 'News' }}</span>
      </template>
      <div class="m-drawer-body" v-if="selectedNews">
        <div class="m-detail-badges">
          <span class="m-news-cat-badge" :class="newsCategoryClass(selectedNews.Category)">
            {{ selectedNews.Category || 'General' }}
          </span>
          <span class="m-news-type-label">{{ selectedNews.IsEvent ? 'Event' : 'News' }}</span>
        </div>
        <h3 class="m-detail-title">{{ selectedNews.Title }}</h3>
        <p class="m-detail-date">
          {{ formatDate(selectedNews.PublishedDate || selectedNews.CreatedDate) }}
        </p>
        <p class="m-detail-text">{{ selectedNews.Description || selectedNews.Content }}</p>
        <button class="m-drawer-close-btn" @click="showNewsDrawer = false">Close</button>
      </div>
    </ONEDrawer>

    <!-- ───────────────────────────────────────────
         FIXED BOTTOM NAVIGATION BAR
    ─────────────────────────────────────────────── -->
    <nav class="m-bottom-nav">
      <button
        class="m-nav-item"
        :class="{ 'm-nav-active': activeBottomTab === 'home' }"
        @click="onNavClick('home')"
      >
        <i class="pi pi-home"></i>
        <span>Home</span>
      </button>
      <button
        class="m-nav-item"
        :class="{ 'm-nav-active': activeBottomTab === 'tasks' }"
        @click="onNavClick('tasks')"
      >
        <i class="pi pi-check-square"></i>
        <span>Tasks</span>
      </button>
      <button
        class="m-nav-item"
        :class="{ 'm-nav-active': activeBottomTab === 'circulars' }"
        @click="onNavClick('circulars')"
      >
        <i class="pi pi-file-edit"></i>
        <span>Circulars</span>
      </button>
      <button
        class="m-nav-item"
        :class="{ 'm-nav-active': activeBottomTab === 'apps' }"
        @click="onNavClick('apps')"
      >
        <i class="pi pi-th-large"></i>
        <span>Apps</span>
      </button>
      <button
        class="m-nav-item"
        :class="{ 'm-nav-active': activeBottomTab === 'more' }"
        @click="onNavClick('more')"
      >
        <i class="pi pi-ellipsis-h"></i>
        <span>More</span>
      </button>
    </nav>

  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { useDashboardData } from './useDashboardData'
import moment from 'moment'

// ─── Composable ─────────────────────────────────────────────────────────────
const {
  loading,
  error,
  modules,
  news,
  selectedNewsCategory,
  newsCategories,
  orgCirculars,
  deptCirculars,
  deptOptions,
  selectedDeptId,
  isAdmin,
  socialPages,
  activeSocialTab,
  birthdays,
  activeTaskTab,
  tasksLoading,
  myTasks,
  deptTasksList,
  currentTasks,
  allSOPs,
  filteredSOPs,
  sopLoading,
  sopSearchTerm,
  selectedCategory,
  sopCategories,
  unreadCount,
  accessibleModules,
  toBePurchasedModules,
  tasksDueToday,
  sopCount,
  taskStats,
  sortedTasks,
  taskColumns,
  loadModules,
  loadNews,
  selectNewsCategory,
  loadDashboardData,
  loadCirculars,
  loadSocialPages,
  loadBirthdays,
  loadMyTasks,
  loadDeptTasks,
  loadSOPs,
  switchTaskTab,
  onSOPSearch,
  applySOPFilter,
  clearSOPSearch,
  selectCategory,
  registerFCMToken,
  handleModuleSelect,
  handleMoceroUIModule,
  handleONEUIModule,
  getModuleByName,
  handleLogout,
  formatDate,
  isPastDue,
  isTaskCompleted,
  timeRemaining,
  dueCellClass,
  getInitials,
  priorityClass,
  statusClass,
  socialIcon,
  scrollTo
} = useDashboardData()

// ─── Auth store (for user display) ──────────────────────────────────────────
const authStore = useAuthStore()

// ─── Local state ────────────────────────────────────────────────────────────
const activeBottomTab = ref('home')
const showDeptDrawer = ref(false)
const selectedCircular = ref<any>(null)
const showCircularDrawer = ref(false)
const selectedNews = ref<any>(null)
const showNewsDrawer = ref(false)

// ─── Merged circulars (org + dept, sorted desc, max 8) ──────────────────────
const mergedCirculars = computed(() => {
  const org = orgCirculars.value.map((c: any) => ({ ...c, _type: 'org' }))
  const dept = deptCirculars.value.map((c: any) => ({ ...c, _type: 'dept' }))
  return [...org, ...dept]
    .sort((a, b) => {
      const da = moment(a.PublishedDate || a.CreatedDate, ['DD-MM-YYYY', moment.ISO_8601])
      const db = moment(b.PublishedDate || b.CreatedDate, ['DD-MM-YYYY', moment.ISO_8601])
      return db.valueOf() - da.valueOf()
    })
    .slice(0, 8)
})

// ─── News category colour helper ────────────────────────────────────────────
function newsCategoryClass(cat: string) {
  const map: Record<string, string> = {
    Clinical: 'news-cat-clinical',
    HR: 'news-cat-hr',
    Emergency: 'news-cat-emergency',
    Operations: 'news-cat-operations',
    Training: 'news-cat-training',
    Policy: 'news-cat-policy',
    General: 'news-cat-general'
  }
  return map[cat] || 'news-cat-general'
}

// ─── Birthday helpers ────────────────────────────────────────────────────────
const todayBirthdays = computed(() => {
  const today = moment()
  return birthdays.value.filter((b: any) => {
    const dob = moment(b.DateOfBirth || b.DOB, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601])
    return dob.isValid() && dob.date() === today.date() && dob.month() === today.month()
  })
})

const upcomingBirthdays = computed(() => {
  const today = moment()
  return birthdays.value.filter((b: any) => {
    const dob = moment(b.DateOfBirth || b.DOB, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601])
    if (!dob.isValid()) return false
    const thisYearBday = moment().month(dob.month()).date(dob.date())
    const diff = thisYearBday.diff(today, 'days')
    return diff > 0 && diff <= 7
  })
})

function formatBirthdayDate(b: any) {
  const dob = moment(b.DateOfBirth || b.DOB, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601])
  return dob.isValid() ? dob.format('DD MMM') : ''
}

// ─── Bottom nav click ────────────────────────────────────────────────────────
function onNavClick(tab: string) {
  activeBottomTab.value = tab
  if (tab === 'home') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else if (tab === 'tasks') {
    scrollTo('m-tasks')
  } else if (tab === 'circulars') {
    scrollTo('m-circulars')
  } else if (tab === 'apps') {
    scrollTo('m-apps')
  } else if (tab === 'more') {
    scrollTo('m-sop')
  }
}
</script>

<style scoped>
/* ─── CSS variables / tokens ────────────────────────────────────────────── */
.mobile-root {
  --bg: #f8fafc;
  --card-bg: #ffffff;
  --card-border: 1px solid var(--one-rule);
  --card-radius: 12px;
  --card-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  --teal: #0d9488;
  --teal-light: #e6f7f6;
  --section-title-color: #0f172a;
  --body-color: #334155;
  --secondary-color: var(--one-muted);
  --header-height: 56px;
  --nav-height: 60px;
  --priority-high-bg: #fef2f2;
  --priority-high-color: #dc2626;
  --priority-high-border: #fecaca;
  --priority-medium-bg: #fffbeb;
  --priority-medium-color: #d97706;
  --priority-medium-border: #fde68a;
  --priority-low-bg: #f0fdf4;
  --priority-low-color: #16a34a;
  --priority-low-border: #bbf7d0;
  --status-completed-bg: #dcfce7;
  --status-completed-color: #16a34a;
  --status-inprogress-bg: #dbeafe;
  --status-inprogress-color: #2563eb;
  --status-pending-bg: var(--one-rule-soft);
  --status-pending-color: var(--one-muted);

  background: var(--bg);
  min-height: 100dvh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--body-color);
  font-size: 14px;
  overflow-x: hidden;
}

/* ─── Header ────────────────────────────────────────────────────────────── */
.m-header {
  position: sticky;
  top: 0;
  z-index: 100;
  height: var(--header-height);
  background: #ffffff;
  border-bottom: 1px solid var(--one-rule);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.m-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 16px;
}

.m-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.m-brand-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--teal);
}

.m-brand-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.m-user-name {
  font-size: 12px;
  color: var(--secondary-color);
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 360px) {
  .m-user-name { display: none; }
}

.m-icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
  font-size: 15px;
  transition: background 0.15s;
}

.m-icon-btn:active { background: var(--one-rule); }

.m-logout-btn { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
.m-logout-btn:active { background: #fee2e2; }

.m-bell-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

/* ─── Pulse strip ───────────────────────────────────────────────────────── */
.m-pulse-strip {
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  background: #ffffff;
  border-bottom: 1px solid var(--one-rule);
}

.m-pulse-strip::-webkit-scrollbar { display: none; }

.m-pulse-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 20px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12px;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
}

.m-pulse-chip:active { background: var(--teal-light); border-color: var(--teal); }

.m-chip-emoji { font-size: 14px; }
.m-chip-label { font-weight: 500; }
.m-chip-count {
  background: var(--teal);
  color: #fff;
  border-radius: 10px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 700;
}

/* ─── Main scrollable content ───────────────────────────────────────────── */
.m-main-content {
  padding: 12px 0;
}

/* ─── Section ───────────────────────────────────────────────────────────── */
.m-section {
  margin: 0 0 16px;
  scroll-margin-top: calc(var(--header-height) + 8px);
}

.m-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 8px;
}

.m-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--section-title-color);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.m-view-all {
  font-size: 12px;
  color: var(--teal);
  font-weight: 600;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

/* ─── Card wrapper ──────────────────────────────────────────────────────── */
.m-card {
  margin: 0 16px;
  background: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  overflow: hidden;
}

/* ─── Badges ────────────────────────────────────────────────────────────── */
.m-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}

.priority-high {
  background: var(--priority-high-bg);
  color: var(--priority-high-color);
  border-color: var(--priority-high-border);
}

.priority-medium {
  background: var(--priority-medium-bg);
  color: var(--priority-medium-color);
  border-color: var(--priority-medium-border);
}

.priority-low {
  background: var(--priority-low-bg);
  color: var(--priority-low-color);
  border-color: var(--priority-low-border);
}

.status-completed {
  background: var(--status-completed-bg);
  color: var(--status-completed-color);
}

.status-inprogress {
  background: var(--status-inprogress-bg);
  color: var(--status-inprogress-color);
}

.status-pending {
  background: var(--status-pending-bg);
  color: var(--status-pending-color);
}

/* ─── Due chip ──────────────────────────────────────────────────────────── */
.m-due-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--secondary-color);
  white-space: nowrap;
}

.due-overdue { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
.due-soon    { background: #fffbeb; color: #d97706; border-color: #fde68a; }
.due-ok      { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
.due-completed { background: var(--status-completed-bg); color: var(--status-completed-color); border-color: #86efac; }

/* ─── Stat row (tasks) ──────────────────────────────────────────────────── */
.m-stat-row {
  display: flex;
  gap: 8px;
  padding: 0 16px 10px;
  overflow-x: auto;
  scrollbar-width: none;
}

.m-stat-row::-webkit-scrollbar { display: none; }

.m-stat-chip {
  flex: 1;
  min-width: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 4px;
  background: var(--card-bg);
  border: var(--card-border);
  border-radius: 10px;
  box-shadow: var(--card-shadow);
}

.m-stat-inprogress { border-color: #bfdbfe; }
.m-stat-overdue    { border-color: #fecaca; }
.m-stat-total      { border-color: #e9d5ff; }

.m-stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
}

.m-stat-label {
  font-size: 10px;
  color: var(--secondary-color);
  text-align: center;
}

/* ─── Tab bar ───────────────────────────────────────────────────────────── */
.m-tab-bar {
  display: flex;
  margin: 0 16px 10px;
  background: var(--one-rule-soft);
  border-radius: 8px;
  padding: 3px;
}

.m-tab-btn {
  flex: 1;
  padding: 7px 0;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--secondary-color);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.m-tab-active {
  background: #ffffff;
  color: var(--teal);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ─── Task list ─────────────────────────────────────────────────────────── */
.m-task-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
  max-height: 400px;
  overflow-y: auto;
}

.m-task-card {
  background: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.m-task-card-top {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.m-task-name {
  font-weight: 600;
  font-size: 13px;
  color: #0f172a;
  flex: 1;
  line-height: 1.4;
}

.m-task-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.m-task-dept {
  font-size: 11px;
  color: var(--secondary-color);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-task-card-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

/* ─── Circular rows ─────────────────────────────────────────────────────── */
.m-circular-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid var(--one-rule-soft);
}

.m-circular-row:last-child { border-bottom: none; }
.m-circular-row:active { background: #f8fafc; }

.m-circ-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
}

.m-circ-dot-org  { background: #3b82f6; }
.m-circ-dot-dept { background: #22c55e; }

.m-circular-info {
  flex: 1;
  min-width: 0;
}

.m-circular-title {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-circular-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.m-circular-date,
.m-circular-dept {
  font-size: 11px;
  color: var(--secondary-color);
}

.m-attach-icon {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--secondary-color);
  margin-top: 3px;
}

/* ─── Admin filter row ──────────────────────────────────────────────────── */
.m-admin-row {
  padding: 0 16px 8px;
}

.m-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--teal);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* ─── Pills scroll ──────────────────────────────────────────────────────── */
.m-pills-scroll {
  display: flex;
  gap: 6px;
  padding: 0 16px 10px;
  overflow-x: auto;
  scrollbar-width: none;
}

.m-pills-scroll::-webkit-scrollbar { display: none; }

.m-pill {
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 16px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--secondary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.m-pill-active {
  background: var(--teal);
  color: #ffffff;
  border-color: var(--teal);
}

/* ─── News cards ────────────────────────────────────────────────────────── */
.m-news-card {
  margin: 0 16px 8px;
  background: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  padding: 12px 14px;
  cursor: pointer;
}

.m-news-card:active { background: #f8fafc; }

.m-news-card-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.m-news-cat-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.news-cat-clinical   { background: #dbeafe; color: #2563eb; }
.news-cat-hr         { background: #fce7f3; color: #be185d; }
.news-cat-emergency  { background: #fef2f2; color: #dc2626; }
.news-cat-operations { background: #fffbeb; color: #d97706; }
.news-cat-training   { background: #f0fdf4; color: #16a34a; }
.news-cat-policy     { background: #f5f3ff; color: #7c3aed; }
.news-cat-general    { background: var(--one-rule-soft); color: #475569; }

.m-news-type-label {
  font-size: 11px;
  color: var(--secondary-color);
  font-weight: 500;
}

.m-news-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.m-news-date {
  font-size: 11px;
  color: var(--secondary-color);
  margin: 0 0 4px;
}

.m-news-desc {
  font-size: 12px;
  color: var(--body-color);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ─── Apps grid ─────────────────────────────────────────────────────────── */
.m-apps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 0 16px;
}

.m-app-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px;
  background: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  cursor: pointer;
  transition: background 0.12s;
}

.m-app-item:active { background: var(--teal-light); }

.m-app-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--teal-light);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--teal);
  font-size: 18px;
}

.m-app-name {
  font-size: 11px;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ─── Search bar ────────────────────────────────────────────────────────── */
.m-search-wrap {
  position: relative;
  margin: 0 16px 10px;
}

.m-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: var(--secondary-color);
  pointer-events: none;
}

.m-search-input {
  width: 100%;
  padding: 9px 36px;
  border: 1px solid var(--one-rule);
  border-radius: 10px;
  background: #ffffff;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.m-search-input:focus { border-color: var(--teal); }

.m-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #cbd5e1;
  color: #fff;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* ─── SOP rows ──────────────────────────────────────────────────────────── */
.m-sop-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--one-rule-soft);
}

.m-sop-row:last-child { border-bottom: none; }

.m-sop-file-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  font-size: 16px;
}

.m-sop-info {
  flex: 1;
  min-width: 0;
}

.m-sop-title {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-sop-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.m-sop-cat-badge {
  padding: 1px 6px;
  border-radius: 5px;
  background: var(--teal-light);
  color: var(--teal);
  font-size: 10px;
  font-weight: 600;
}

.m-sop-version {
  font-size: 10px;
  color: var(--secondary-color);
}

.m-sop-download {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--teal);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  text-decoration: none;
  transition: background 0.12s;
}

.m-sop-download:active { background: var(--teal-light); }

/* ─── Empty state ───────────────────────────────────────────────────────── */
.m-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 16px;
  color: var(--secondary-color);
  font-size: 13px;
}

.m-empty-icon {
  font-size: 28px;
  opacity: 0.4;
}

/* ─── Loading row ───────────────────────────────────────────────────────── */
.m-loading-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: var(--secondary-color);
  font-size: 13px;
}

/* ─── Drawer styles ─────────────────────────────────────────────────────── */
.m-drawer-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.m-drawer-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
}

.m-drawer-close-btn {
  align-self: flex-end;
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid var(--one-rule);
  background: #f8fafc;
  color: var(--body-color);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.m-drawer-close-btn:active { background: var(--one-rule); }

/* ─── Detail view (inside drawers) ─────────────────────────────────────── */
.m-detail-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.m-badge-org  { background: #dbeafe; color: #2563eb; border-color: #bfdbfe; }
.m-badge-dept { background: #dcfce7; color: #16a34a; border-color: #bbf7d0; }

.m-detail-dept {
  font-size: 12px;
  color: var(--secondary-color);
}

.m-detail-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  line-height: 1.4;
}

.m-detail-date {
  font-size: 12px;
  color: var(--secondary-color);
  margin: 0 0 12px;
}

.m-detail-text {
  font-size: 13px;
  color: var(--body-color);
  line-height: 1.6;
  margin: 0 0 12px;
  white-space: pre-line;
}

.m-detail-html {
  font-size: 13px;
  color: var(--body-color);
  line-height: 1.6;
  margin-bottom: 12px;
}

.m-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 9px;
  background: var(--teal);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
}

.m-download-btn:active { opacity: 0.85; }

/* ─── Bottom nav ────────────────────────────────────────────────────────── */
.m-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  height: calc(var(--nav-height) + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: #ffffff;
  border-top: 1px solid var(--one-rule);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.m-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.m-nav-item i {
  font-size: 18px;
  line-height: 1;
}

.m-nav-active {
  color: var(--teal);
}

.m-nav-active i {
  font-weight: 700;
}

/* ─── Bottom spacer so content clears fixed nav ─────────────────────────── */
.m-bottom-spacer {
  height: calc(var(--nav-height) + env(safe-area-inset-bottom, 0px) + 8px);
}

/* ─── App logo image ─────────────────────────────────────────────────────── */
.m-app-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
}

/* ─── Birthday Section ───────────────────────────────────────────────────── */
.m-birthday-today-card {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
}

.m-birthday-today-header {
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
  padding: 10px 14px 4px;
}

.m-birthday-upcoming-header {
  font-size: 13px;
  font-weight: 600;
  color: var(--secondary-color);
  padding: 10px 14px 4px;
}

.m-birthday-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--one-rule-soft);
}

.m-birthday-row:last-child {
  border-bottom: none;
}

.m-birthday-avatar,
.m-birthday-avatar-upcoming {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.m-birthday-avatar {
  background: #fef3c7;
}

.m-birthday-avatar-upcoming {
  background: #e0f2fe;
}

.m-birthday-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.m-birthday-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-birthday-dept {
  font-size: 11px;
  color: var(--secondary-color);
}

.m-chip-count-header {
  font-size: 12px;
  font-weight: 600;
  color: var(--teal);
  background: var(--teal-light);
  padding: 2px 8px;
  border-radius: 10px;
}
</style>
